interface MercadoPagoConfig {
  publicKey: string;
  locale?: string;
  deviceId?: string;
}

interface MercadoPagoInstance {
  bricks: () => any;
  fields: any;
}

class MercadoPagoService {
  private static instance: MercadoPagoService;
  private mpInstance: MercadoPagoInstance | null = null;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): MercadoPagoService {
    if (!MercadoPagoService.instance) {
      MercadoPagoService.instance = new MercadoPagoService();
    }
    return MercadoPagoService.instance;
  }

  public async initialize(config: MercadoPagoConfig): Promise<void> {
    if (this.isInitialized && this.mpInstance) {
      return;
    }

    if (this.initializationPromise) {
      return await this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization(config);
    return await this.initializationPromise;
  }

  private async performInitialization(config: MercadoPagoConfig): Promise<void> {
    try {
      // Verificar si el SDK ya está cargado
      if (window.MercadoPago) {
        this.createMercadoPagoInstance(config);
        return;
      }

      // Cargar el SDK si no está disponible
      await this.loadSDK();
      this.createMercadoPagoInstance(config);
    } catch (error) {
      console.error("Error inicializando MercadoPago:", error);
      throw new Error("No se pudo inicializar MercadoPago");
    }
  }

  private loadSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verificar si ya existe el script
      const existingScript = document.getElementById("mercado-pago-sdk");
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.id = "mercado-pago-sdk";
      script.src = "https://sdk.mercadopago.com/js/v2";
      script.async = true;

      script.onload = () => {
        console.log("SDK de MercadoPago cargado correctamente");
        resolve();
      };

      script.onerror = () => {
        reject(new Error("Error al cargar el SDK de MercadoPago"));
      };

      document.head.appendChild(script);
    });
  }

  private createMercadoPagoInstance(config: MercadoPagoConfig): void {
    if (!window.MercadoPago) {
      throw new Error("SDK de MercadoPago no disponible");
    }

    // Configuración base
    const mpConfig: any = {
      locale: config.locale || "es-AR",
    };

    // Incluir Device ID si está disponible
    if (config.deviceId) {
      mpConfig.deviceId = config.deviceId;
    }

    this.mpInstance = new window.MercadoPago(config.publicKey, mpConfig);

    this.isInitialized = true;
    console.log("Instancia de MercadoPago creada correctamente", config.deviceId ? "con Device ID" : "sin Device ID");
  }

  /**
   * Obtiene la instancia de MercadoPago
   * Debe llamarse después de initialize()
   */
  public getInstance(): MercadoPagoInstance {
    if (!this.mpInstance || !this.isInitialized) {
      throw new Error("MercadoPago no ha sido inicializado. Llama a initialize() primero.");
    }
    return this.mpInstance;
  }

  /**
   * Verifica si MercadoPago está inicializado
   */
  public isReady(): boolean {
    return this.isInitialized && this.mpInstance !== null;
  }

  /**
   * Obtiene el Device ID de MercadoPago si está disponible
   */
  public getDeviceId(): string | null {
    if (typeof window !== 'undefined' && (window as any).MP_DEVICE_SESSION_ID) {
      return (window as any).MP_DEVICE_SESSION_ID;
    }
    return null;
  }

  /**
   * Verifica el estado de los scripts de MercadoPago
   */
  public getScriptsStatus(): {
    sdkLoaded: boolean;
    securityScriptLoaded: boolean;
    deviceIdAvailable: boolean;
    deviceId: string | null;
  } {
    return {
      sdkLoaded: typeof window !== 'undefined' && !!window.MercadoPago,
      securityScriptLoaded: typeof document !== 'undefined' && !!document.getElementById('mercado-pago-security'),
      deviceIdAvailable: typeof window !== 'undefined' && !!(window as any).MP_DEVICE_SESSION_ID,
      deviceId: this.getDeviceId(),
    };
  }

  /**
   * Limpia la instancia (útil para testing o reinicialización)
   */
  public cleanup(): void {
    this.mpInstance = null;
    this.isInitialized = false;
    this.initializationPromise = null;
  }
}

export { MercadoPagoService };
export type { MercadoPagoConfig, MercadoPagoInstance }; 